import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import ContactPhoneRounded from '@mui/icons-material/ContactPhoneRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import FavoriteRounded from '@mui/icons-material/FavoriteRounded';
import MailRounded from '@mui/icons-material/MailRounded';
import PhoneRounded from '@mui/icons-material/PhoneRounded';
import TSButton from 'components/common/Button/Button';
import FactList from 'components/common/FactList/FactList';
import TSCard from 'components/common/TSCard/TSCard';
import StatusBadge from 'components/common/StatusBadge/StatusBadge';
import {
  useAddEmergencyContactMutation,
  useUpdateEmergencyContactMutation,
} from 'app/api/usersApiSlice';

const EmergencyContact = ({ contact, index, isModal = false, toggleModal }) => {
  const [contactPhone, setContactPhone] = useState(contact?.ContactPhone || '');
  const [contactName, setContactName] = useState(contact?.ContactName || '');
  const [contactEmail, setContactEmail] = useState(contact?.ContactEmail || '');
  const [relationship, setRelationship] = useState(contact?.Relationship || '');
  const [isPrimary, setIsPrimary] = useState(contact?.IsPrimary || false);
  const [editMode, setEditMode] = useState(!contact ? true : false);
  const [phoneError, setPhoneError] = useState(false);

  const [
    addEmergencyContact,
    {
      data: addEmergencyContactData,
      isLoading: isAddEmergencyContactLoading,
      isError: isAddEmergencyContactError,
      isSuccess: isAddEmergencyContactSuccess,
    },
  ] = useAddEmergencyContactMutation();

  const [
    updateEmergencyContact,
    {
      data: updateEmergencyContactData,
      isLoading: isUpdateEmergencyContactLoading,
      isError: isUpdateEmergencyContactError,
      isSuccess: isUpdateEmergencyContactSuccess,
    },
  ] = useUpdateEmergencyContactMutation();

  const handleCancel = () => {
    setContactPhone(contact?.ContactPhone || '');
    setContactName(contact?.ContactName || '');
    setContactEmail(contact?.ContactEmail || '');
    setRelationship(contact?.Relationship || '');
    setIsPrimary(contact?.IsPrimary || false);
    setPhoneError(false);
    setEditMode(false);
    if (isModal) toggleModal(false);
  };

  const handleSave = () => {
    // Call API to save contact details
    // On success, update local state and exit edit mode

    if (!matchIsValidTel(contactPhone)) {
      setPhoneError(true);
      return;
    }
    setPhoneError(false);

    if (contact?.EmergencyContactID) {
      updateEmergencyContact({
        contact_id: contact.EmergencyContactID,
        full_name: contactName,
        email_address: contactEmail,
        phone_number: contactPhone,
        relationship,
        is_primary: isPrimary,
      });
    } else {
      addEmergencyContact({
        full_name: contactName,
        email_address: contactEmail,
        phone_number: contactPhone,
        relationship,
        is_primary: isPrimary,
      });
    }
    setEditMode(false);
    if (isModal) toggleModal(false);
  };

  if (contact && !editMode) {
    return (
      <TSCard
        icon={<ContactPhoneRounded />}
        title={contactName}
        action={isPrimary && <StatusBadge status="default" />}
      >
        <FactList>
          <li>
            <PhoneRounded aria-hidden="true" />
            <a className="ts-contact-link" href={`tel:${contactPhone}`}>
              {contactPhone}
            </a>
          </li>
          {contactEmail && (
            <li>
              <MailRounded aria-hidden="true" />
              <a className="ts-contact-link" href={`mailto:${contactEmail}`}>
                {contactEmail}
              </a>
            </li>
          )}
          {relationship && (
            <li>
              <FavoriteRounded aria-hidden="true" />
              <span>{relationship}</span>
            </li>
          )}
        </FactList>
        <div>
          <TSButton
            btnText="Edit contact"
            btnVariant="outlined"
            icon={<EditRounded />}
            onClickFunc={() => setEditMode(true)}
          />
        </div>
      </TSCard>
    );
  }

  const form = (
    <div className="flex flex-col gap-4">
      {!contact && (
        <Typography variant="h2">Add an emergency contact</Typography>
      )}
      <Typography variant="body1" color="text.secondary">
        We contact this person if you do not come back from a walk on time.
      </Typography>
      <TextField
        id={`contact-name-${index}`}
        label="Name"
        defaultValue={contactName}
        onChange={(e) => setContactName(e.target.value)}
      />
      <MuiTelInput
        id={`contact-phone-${index}`}
        label="Phone"
        defaultCountry="AU"
        value={contactPhone}
        onChange={(value) => {
          setContactPhone(value);
          setPhoneError(false);
        }}
        error={phoneError}
        helperText={
          phoneError
            ? 'Enter a valid phone number, including the country code.'
            : ''
        }
      />
      <TextField
        id={`contact-email-${index}`}
        label="Email (optional)"
        defaultValue={contactEmail}
        onChange={(e) => setContactEmail(e.target.value)}
      />
      <TextField
        id={`relationship-${index}`}
        label="Relationship"
        helperText="For example: partner, parent, friend."
        defaultValue={relationship}
        onChange={(e) => setRelationship(e.target.value)}
      />
      <FormControlLabel
        control={<Checkbox defaultChecked={isPrimary} />}
        label="Use this contact by default"
        onChange={(e) => setIsPrimary(e.target.checked)}
      />
      <div className="grid grid-cols-2 gap-3">
        <TSButton btnText="Save" onClickFunc={() => handleSave()} />
        <TSButton
          btnText="Cancel"
          btnVariant="outlined"
          onClickFunc={() => handleCancel()}
        />
      </div>
    </div>
  );

  return contact ? <TSCard>{form}</TSCard> : form;
};

export default EmergencyContact;
